import type { LoadDescriptionT, Presence } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import moment from 'moment-timezone';
import { ReactNode, useCallback, useEffect, useState } from 'react';

import { useAppNavigate } from 'utils/url/useAppNavigate';

import { PresenceOverlayPopover } from './PresenceOverlayPopover';

export type OwnProps = {
  isSimultaneousRingLoading: boolean;
  user: User;
  presence: Presence;
  ownPresence: Presence;
  ownAgentId: string;
  isOwnAgent: boolean;
  internalCall: (payload: any) => Promise<any>;
  showUserModal: (payload: { user: User }) => void;
  presenceSetOthersSimultaneousRinging: (payload: any) => Promise<void>;
  canMakeInternalCall: boolean;
  favorites: readonly string[];
  personalAddToFavouriteTeamMemberIds: (payload: {
    agentId: string;
  }) => Promise<void>;
  personalRemoveFromFavouriteTeamMemberIds: (payload: {
    agentId: string;
  }) => Promise<void>;
  children?: ReactNode;
};

function isConnectedToOwnAgent(props: OwnProps) {
  let isConnectedWithOwnAgent = false;
  let isInternalCallCalling = false;
  let internalBtnText = 'Call';

  const internalCalls = props.presence.presence_status.load_descriptions.filter(
    (o) =>
      o.load_type === 'internalwork' && o.conversation_channel === 'pstnphone',
  );

  const ownInternalCalls =
    props.ownPresence.presence_status.load_descriptions.filter(
      (o) =>
        o.load_type === 'internalwork' &&
        o.conversation_channel === 'pstnphone',
    );

  const numberOfAgentsActiveCalls =
    props.presence.presence_status.load_descriptions.filter(
      (o) =>
        (o.conversation_channel === 'pstnphone' ||
          o.conversation_channel === 'callback') &&
        o.load_type !== 'acw' &&
        o.load_type !== 'nonlive',
    ).length;

  for (const ownInternalCall of ownInternalCalls) {
    for (const internalCall of internalCalls) {
      if (ownInternalCall.csid === internalCall.csid) {
        isConnectedWithOwnAgent = true;
        internalBtnText = 'Connected';

        if (
          ownInternalCall.requester !== null &&
          ownInternalCall.requester.id === props.ownAgentId
        ) {
          if (internalCall?.conversation?.assignment !== null) {
            // Internal was started by own and accepted - show connected.
          } else {
            // Internal was started by own - show calling at btn
            isInternalCallCalling = true;
            internalBtnText = 'Connected';
          }
          break;
        }
        break;
      }
    }
  }

  let disabledCall = '';
  if (
    props.canMakeInternalCall === false ||
    isConnectedWithOwnAgent === true ||
    props.isOwnAgent === true ||
    numberOfAgentsActiveCalls > 0 ||
    props.presence.status === 'offline' ||
    isInternalCallCalling === true
  ) {
    disabledCall = 'disabled';
  }

  return { internalBtnText, disabledCall };
}

const PresenceOverlay = (props: OwnProps) => {
  const [internalBtnText, setInternalBtnText] = useState('Call');
  const [disabledCall, setDisabledCall] = useState(() => {
    return props.presence.status === 'offline' ||
      props.isOwnAgent === true ||
      props.canMakeInternalCall === false
      ? 'disabled'
      : '';
  });

  const navigate = useAppNavigate();

  // Replicates componentDidUpdate for relevant props
  useEffect(() => {
    const { internalBtnText, disabledCall } = isConnectedToOwnAgent(props);
    setInternalBtnText(internalBtnText);
    setDisabledCall(disabledCall);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.presence,
    props.ownPresence,
    props.ownAgentId,
    props.canMakeInternalCall,
    props.isOwnAgent,
  ]);

  const onInternalCallClickHandler = useCallback(() => {
    if (props.canMakeInternalCall === true) {
      setInternalBtnText('Calling...');
      props.internalCall({ userId: props.user.id });
    }
  }, [props]);

  const handlePresenceLinkClick = useCallback(
    (csid: LoadDescriptionT['csid']) => {
      navigate({
        path: '/conversation/:csid',
        params: { path: { csid: `${csid}` } },
      });
    },
    [props],
  );

  const addToFavorites = useCallback(
    (agentId: string) => {
      props.personalAddToFavouriteTeamMemberIds({ agentId });
    },
    [props.personalAddToFavouriteTeamMemberIds],
  );

  const removeFromFavorites = useCallback(
    (agentId: string) => {
      props.personalRemoveFromFavouriteTeamMemberIds({ agentId });
    },
    [props.personalRemoveFromFavouriteTeamMemberIds],
  );

  const { presence } = props;
  const {
    presence_status: { load_descriptions },
  } = presence;

  const {
    externalPhoneConversations,
    nonelivePhoneConversations,
    // internalPhoneConversations,
    chatConversations,
    messengerConversations,
    voicemailConversations,
    emailConversations,
    contactformConversations,
    callbackConversations,
    facebookMessengerConversations,
    acwConversations,
    unknownChannelConversations,
  } = load_descriptions.reduce<
    Record<
      | 'externalPhoneConversations'
      | 'nonelivePhoneConversations'
      | 'internalPhoneConversations'
      | 'chatConversations'
      | 'messengerConversations'
      | 'voicemailConversations'
      | 'emailConversations'
      | 'contactformConversations'
      | 'callbackConversations'
      | 'facebookMessengerConversations'
      | 'acwConversations'
      | 'unknownChannelConversations',
      LoadDescriptionT[]
    >
  >(
    (acc, description) => {
      if (description.load_type === 'acw') {
        acc.acwConversations.push(description);
        return acc;
      }

      switch (description.conversation_channel) {
        case 'pstnphone': {
          switch (description.load_type) {
            case 'externalwork':
              acc.externalPhoneConversations.push(description);
              break;
            case 'nonlive':
              acc.nonelivePhoneConversations.push(description);
              break;
            case 'internalwork':
              acc.internalPhoneConversations.push(description);
              break;
          }
          break;
        }
        case 'widgetchat':
          acc.chatConversations.push(description);
          break;
        case 'voicemail':
          acc.voicemailConversations.push(description);
          break;
        case 'email':
          acc.emailConversations.push(description);
          break;
        case 'messenger':
          acc.messengerConversations.push(description);
          break;
        case 'facebookmessenger':
          acc.facebookMessengerConversations.push(description);
          break;
        case 'contactform':
          acc.contactformConversations.push(description);
          break;
        case 'callback':
          acc.callbackConversations.push(description);
          break;
        default:
          acc.unknownChannelConversations.push(description);
      }

      return acc;
    },
    {
      externalPhoneConversations: [],
      nonelivePhoneConversations: [],
      internalPhoneConversations: [],
      chatConversations: [],
      messengerConversations: [],
      voicemailConversations: [],
      emailConversations: [],
      contactformConversations: [],
      callbackConversations: [],
      facebookMessengerConversations: [],
      acwConversations: [],
      unknownChannelConversations: [],
    },
  );

  const numberOfAcw = acwConversations.length;

  let lastSeen: string | null = null;
  let acwConversationNode: JSX.Element | null = null;
  if (numberOfAcw > 0) {
    const conversation = numberOfAcw === 1 ? 'conversation' : 'conversations';
    acwConversationNode = (
      <span>
        Wrap-up time on {numberOfAcw} {conversation}
      </span>
    );
  }

  if (props.presence.status === 'offline') {
    lastSeen =
      props.presence.last_seen === null
        ? 'Has not signed-in yet'
        : `Last seen ${moment(props.presence.last_seen).fromNow()}`;
  }

  return (
    <PresenceOverlayPopover
      user={props.user}
      presence={presence}
      acwConversationNode={acwConversationNode}
      lastSeen={lastSeen}
      onInternalCallClickHandler={onInternalCallClickHandler}
      isSimultaneousRingLoading={props.isSimultaneousRingLoading}
      presenceSetOthersSimultaneousRinging={
        props.presenceSetOthersSimultaneousRinging
      }
      isOwnAgent={props.isOwnAgent}
      internalBtnText={internalBtnText}
      disabledCall={disabledCall}
      showUserModal={props.showUserModal}
      conversationsByChannel={{
        clock: acwConversations,
        phone: [...externalPhoneConversations, ...nonelivePhoneConversations],
        chat: chatConversations,
        messenger: [...messengerConversations, ...unknownChannelConversations],
        voicemail: voicemailConversations,
        mail: emailConversations,
        'chat-widget': contactformConversations,
        callback: callbackConversations,
        'facebook-messenger': facebookMessengerConversations,
      }}
      handlePresenceLinkClick={handlePresenceLinkClick}
      isFavorite={props.favorites.includes(props.user.id)}
      addToFavorites={addToFavorites}
      removeFromFavorites={removeFromFavorites}
    >
      {props.children}
    </PresenceOverlayPopover>
  );
};

export default PresenceOverlay;
